import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Avatar,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Card,
  CardContent,
  InputAdornment,
  Grid,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import { useTranslation } from "react-i18next";
import { fetchJson } from "../../../utils/fetchJson";
import { UserContext } from "../../../contexts/UserContext";
import { useTheme } from "@mui/material/styles";
import ErrorBoundary from "../../../components/ErrorBoundary";

export default function RegistrationQueuePage() {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const theme = useTheme();

  const canFeature = React.useCallback((permKey) => {
    if (user?.role === 'SystemAdmin') return true;
    const perms = user && Array.isArray(user.systemPermissions) ? user.systemPermissions : null;
    if (!perms) return true;
    if (perms.includes('*')) return true;
    return perms.includes(permKey);
  }, [user]);

  const canReview = canFeature('registration-queue');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [metrics, setMetrics] = useState({
    total: 0,
    highPriority: 0,
    mediumPriority: 0,
    lowPriority: 0,
  });

  useEffect(() => {
    loadRequests();
    loadMetrics();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const data = await fetchJson("/api/registration/queue");
      setRequests(data.requests);
    } catch (e) {
      setError(`Failed to load requests: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    try {
      const data = await fetchJson("/api/registration/metrics");
      setMetrics(data);
    } catch (e) {
      console.error("Failed to load metrics:", e);
      setError(`Failed to load metrics: ${e.message}`);
      setMetrics({
        total: 0,
        highPriority: 0,
        mediumPriority: 0,
        lowPriority: 0,
      });
    }
  };

  const openReview = (request) => {
    setSelectedRequest(request);
    setRejectReason("");
    setReviewOpen(true);
  };

  const closeReview = () => {
    setReviewOpen(false);
    setSelectedRequest(null);
  };

  const handleApprove = async () => {
    if (!canReview) return;
    try {
      await fetchJson(`/api/registration/${selectedRequest.id}/approve`, {
        method: "POST",
      });
      setReviewOpen(false);
      loadRequests();
      loadMetrics();
    } catch (e) {
      setError(t("queuePage.approveError") || "Failed to approve request");
    }
  };

  const handleReject = async () => {
    if (!canReview) return;
    try {
      await fetchJson(`/api/registration/${selectedRequest.id}/reject`, {
        method: "POST",
        body: JSON.stringify({ reason: rejectReason }),
      });
      setReviewOpen(false);
      loadRequests();
      loadMetrics();
    } catch (e) {
      setError(t("queuePage.rejectError") || "Failed to reject request");
    }
  };

  const handleBulkAction = async (action) => {
    if (
      !window.confirm(
        t("queuePage.confirmBulk", { action }) ||
          `Are you sure you want to ${action} all selected?`
      )
    )
      return;

    try {
      // Implement bulk action logic
      loadRequests();
      loadMetrics();
    } catch (e) {
      setError(t("queuePage.bulkError") || "Bulk operation failed");
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 120,
    },
    {
      field: "schoolName",
      headerName: t("queuePage.schoolName") || "School Name",
      flex: 1,
    },
    {
      field: "submittedBy",
      headerName: t("queuePage.submittedBy") || "Submitted By",
      flex: 1,
      renderCell: (params) => (
        <div>
          <div>{params.value.name}</div>
          <div style={{ fontSize: 12, color: theme.palette.text.secondary }}>
            {params.value.email}
          </div>
        </div>
      ),
    },
    {
      field: "submissionDate",
      headerName: t("queuePage.submittedOn") || "Submitted On",
      width: 150,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: "documents",
      headerName: t("queuePage.documents") || "Documents",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          {params.value.map((doc, index) => (
            <IconButton
              key={index}
              size="small"
              onClick={() => window.open(doc.url, "_blank")}
            >
              <DescriptionIcon fontSize="small" />
            </IconButton>
          ))}
        </Box>
      ),
    },
    {
      field: "priority",
      headerName: t("queuePage.priority") || "Priority",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === "high"
              ? "error"
              : params.value === "medium"
              ? "warning"
              : "success"
          }
          size="small"
        />
      ),
    },
    {
      field: "schoolType",
      headerName: t("queuePage.schoolType") || "Type",
      width: 120,
      renderCell: (params) => {
        const labels = { private: 'Private', confessional: 'Confessional', public: 'Public' };
        return labels[params.value] || params.value || '—';
      },
    },
    {
      field: "region",
      headerName: t("queuePage.region") || "Region",
      width: 130,
      renderCell: (params) => params.value || '—',
    },
    {
      field: "actions",
      headerName: t("queuePage.actions") || "Actions",
      width: 120,
      renderCell: (params) => (
        <Button
          variant="outlined"
          size="small"
          onClick={() => openReview(params.row)}
        >
          {t("queuePage.review") || "Review"}
        </Button>
      ),
    },
  ];

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 4 },
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h4" gutterBottom>
        {t("queuePage.title") || "Registration Queue"}
      </Typography>

      {/* Metrics Bar */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Badge badgeContent={metrics.total} color="primary" sx={{ mr: 2 }}>
          <Typography variant="h6">
            {t("queuePage.pendingRequests") || "Pending Requests"}
          </Typography>
        </Badge>

        <Chip
          label={`${t("queuePage.highPriority") || "High"}: ${
            metrics.highPriority
          }`}
          color="error"
          size="small"
        />
        <Chip
          label={`${t("queuePage.mediumPriority") || "Medium"}: ${
            metrics.mediumPriority
          }`}
          color="warning"
          size="small"
        />
        <Chip
          label={`${t("queuePage.lowPriority") || "Low"}: ${
            metrics.lowPriority
          }`}
          color="success"
          size="small"
        />
      </Box>

      {/* Action Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            size="small"
            placeholder={t("queuePage.search") || "Search..."}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              loadRequests();
              loadMetrics();
            }}
          >
            {t("queuePage.refresh") || "Refresh"}
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            color="success"
            onClick={() => handleBulkAction("approve")}
            disabled={!canReview}
          >
            {t("queuePage.approveAll") || "Approve All"}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleBulkAction("reject")}
            disabled={!canReview}
          >
            {t("queuePage.rejectAll") || "Reject All"}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <ErrorBoundary>
        <Box sx={{ flex: 1, height: "100%" }}>
          {columns.length > 0 && Array.isArray(requests) && requests.length > 0 ? (
            <DataGrid
              rows={requests}
              columns={columns}
              pageSizeOptions={[10, 25, 50]}
              loading={loading}
              rowHeight={75}
              sx={{
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f8f9fa",
                  borderBottom: "2px solid #e0e0e0",
                },
              }}
            />
          ) : (
            <Typography variant="body2" color="textSecondary" align="center" sx={{ mt: 4 }}>
              {loading ? (t("queuePage.loading") || "Loading...") : (t("queuePage.noRequests") || "No registration requests found.")}
            </Typography>
          )}
        </Box>
      </ErrorBoundary>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onClose={closeReview} maxWidth="md" fullWidth>
        <DialogTitle>
          {t("queuePage.reviewRequest") || "Review Registration Request"}
        </DialogTitle>
        <DialogContent dividers>
          {selectedRequest && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  {t("queuePage.schoolDetails") || "School Details"}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    {t("queuePage.schoolName") || "School Name"}
                  </Typography>
                  <Typography>{selectedRequest.schoolName}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    {t("queuePage.address") || "Address"}
                  </Typography>
                  <Typography>{selectedRequest.address}</Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    {t("queuePage.contact") || "Contact"}
                  </Typography>
                  <Typography>
                    {selectedRequest.contactName} (
                    {selectedRequest.contactEmail})
                    {selectedRequest.contactPhone ? ` | ${selectedRequest.contactPhone}` : ''}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>
                  {t("queuePage.submissionDetails") || "Submission Details"}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    {t("queuePage.submittedBy") || "Submitted By"}
                  </Typography>
                  <Typography>
                    {selectedRequest.submittedBy.name} (
                    {selectedRequest.submittedBy.email})
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    {t("queuePage.submittedOn") || "Submitted On"}
                  </Typography>
                  <Typography>
                    {new Date(selectedRequest.submissionDate).toLocaleString()}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    {t("queuePage.priority") || "Priority"}
                  </Typography>
                  <Chip
                    label={selectedRequest.priority}
                    color={
                      selectedRequest.priority === "high"
                        ? "error"
                        : selectedRequest.priority === "medium"
                        ? "warning"
                        : "success"
                    }
                  />
                </Box>

                <Box>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                  >
                    {t("queuePage.documents") || "Documents"}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {selectedRequest.documents.map((doc, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        startIcon={<DescriptionIcon />}
                        onClick={() => window.open(doc.url, "_blank")}
                      >
                        {doc.name}
                      </Button>
                    ))}
                  </Box>
                </Box>
              </Grid>

              {/* School Credentials / Authenticity Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {t("queuePage.credentials") || "School Credentials"}
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary', width: 200 }}>
                        {t("queuePage.schoolType") || "School Type"}
                      </TableCell>
                      <TableCell>
                        {({ private: 'Private (Lay)', confessional: 'Confessional', public: 'Public' })[selectedRequest.schoolType] || selectedRequest.schoolType || '—'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {t("queuePage.founderName") || "Founder / Proprietor"}
                      </TableCell>
                      <TableCell>{selectedRequest.founderName || '—'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {t("queuePage.founderNationalId") || "Founder National ID (CNI)"}
                      </TableCell>
                      <TableCell>{selectedRequest.founderNationalId || '—'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {t("queuePage.region") || "Region"}
                      </TableCell>
                      <TableCell>{selectedRequest.region || '—'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                        {t("queuePage.town") || "Town / City"}
                      </TableCell>
                      <TableCell>{selectedRequest.town || '—'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Grid>

              {/* Arrêté d'Ouverture Documents */}
              {Array.isArray(selectedRequest.arreteDocuments) && selectedRequest.arreteDocuments.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ mt: 1 }}>
                    {t("queuePage.arreteTitle") || "Arrêté d'Ouverture (Opening Order)"}
                  </Typography>
                  {selectedRequest.arreteDocuments.map((doc, idx) => {
                    const ministryLabels = {
                      MINEDUB: 'MINEDUB (Basic Education)',
                      MINESEC: 'MINESEC (Secondary Education)',
                      MINESUP: 'MINESUP (Higher Education)',
                      MINEFOP: 'MINEFOP (Vocational/Technical)'
                    };
                    const phaseLabels = {
                      nur_pri: t("queuePage.phaseNurPri") || 'Nursery & Primary',
                      sec: t("queuePage.phaseSec") || 'Secondary'
                    };
                    return (
                      <Box key={idx} sx={{ mb: 1.5, p: 1.5, bgcolor: '#F5F7FA', borderRadius: 1, border: '1px solid #E3F2FD' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {phaseLabels[doc.phaseCode] || doc.phaseCode} — {ministryLabels[doc.ministry] || doc.ministry}
                        </Typography>
                        <Typography variant="body2">
                          {t("queuePage.arreteRef") || "Reference"}: <strong>{doc.referenceNumber || '—'}</strong>
                          {doc.issueDate ? ` | ${t("queuePage.arreteDate") || "Date"}: ${new Date(doc.issueDate).toLocaleDateString()}` : ''}
                        </Typography>
                      </Box>
                    );
                  })}
                </Grid>
              )}
            </Grid>
          )}

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t("queuePage.rejectReason") ||
                "Rejection Reason (if applicable)"}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={
                t("queuePage.reasonPlaceholder") ||
                "Enter reason for rejection..."
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReview}>
            {t("queuePage.cancel") || "Cancel"}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={handleReject}
            disabled={!canReview}
          >
            {t("queuePage.reject") || "Reject"}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={handleApprove}
            disabled={!canReview}
          >
            {t("queuePage.approve") || "Approve"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
